import { ContactCardFragment } from "src/app/api"

export type Column<T extends Object> =
  BasicColumn<T>
  | ChipColumn<T>
  | GroupNameColumn<T>
  | BooleanColumn<T>
  | DateColumn<T>
  | HtmlColumn<T>
  | PhoneColumn<T>
  | EmailColumn<T>
  | GroupArrayColumn<T>
  | PercentageColumn<T>
  | LogTypeColumn<T>
  | PartnerNameWithLinkColumn<T>
  | LinkColumn<T>
  | WebcastWithUrlColumn<T>
  | NumberColumn<T>
  | TextColumn<T>
  | tripIdwithLinkColumn<T>
  | linkToPpmColumn<T>
  | groupIdWithLinkColumn<T>
  | FractionColumn<T>
  | ListColumn<T>



interface ColumnBase<T extends Object> {
  field: Extract<keyof T, string>,
  header: string,
  headerTooltip?: string,
  columnClass?: string,
  headerClass?: string,
  sortable?: boolean,
  filterable?: boolean,
  filterField?: string,
  hide?: boolean,
  noExport?: boolean,
  frozen?: boolean,
  styleFunction?: (rowData: T) => Partial<CSSStyleDeclaration>,
  clickeable?: boolean,
  filterTableRef?: string,
  filterColumnIndex?: number,
  filterColumnField?: string,
  includeTooltip?: boolean
  tooltipValue?: string | { field: keyof T} | ((rowData: T) => string)
    tooltipPosition?: "top" | "bottom" | "left" | "right"
}



export interface BasicColumn<T extends Object> extends ColumnBase<T> {

  type: "currency"


}

export interface TextColumn<T extends Object> extends ColumnBase<T> {
  type: "text" | "string",
  html?: boolean
}

export interface NumberColumn<T extends Object> extends ColumnBase<T> {
  type: "number"
  pipeParams?: string
}

export interface DateColumn<T extends Object> extends ColumnBase<T> {
  type: "date"
  dateFormat?: string
  /** Display-only suffix from a sibling installment summary field; sort/export stay on `field`. */
  installmentSummaryField?: keyof T
}

export interface HtmlColumn<T extends Object> extends ColumnBase<T> {
  type: "html"
}

export interface PhoneColumn<T extends Object> extends ColumnBase<T> {
  type: "phone"
}

export interface EmailColumn<T extends Object> extends ColumnBase<T> {
  type: "email"
}

export interface BooleanColumn<T extends Object> extends ColumnBase<T> {
  type: "boolean"
  trueValue?: string
  falseValue?: string
  highlightTrue?: boolean
  highlightFalse?: boolean
  severityMap?: { true: SeverityMapValues, false: SeverityMapValues }
}

export interface PercentageColumn<T extends Object> extends ColumnBase<T> {
  type: "percentage"
}

export interface LogTypeColumn<T extends Object> extends ColumnBase<T> {
  type: 'logtype'
}

export interface GroupNameColumn<T extends Object> extends ColumnBase<T> {
  type: "groupNameWithLink",
  custIdField: keyof T,
  tripIdField?: keyof T,
  fragment?: ContactCardFragment
  showCustId?: boolean
}

export interface PartnerNameWithLinkColumn<T extends Object> extends ColumnBase<T> {
  type: "partnerNameWithLink",
  partnerIdField: keyof T,
  custId?: number,
  showPartnerId?: boolean
}

export interface GroupArrayColumn<T extends Object> extends ColumnBase<T> {
  type: "groupArray"
  displayField: "custId" | "groupName"
  fragment?: ContactCardFragment
}

export interface tripIdwithLinkColumn<T extends Object> extends ColumnBase<T> {
  type: "tripIdWithLink"
  tripIdField: keyof T,
  custIdField: keyof T,
  fragment?: ContactCardFragment
  showCustId?: boolean
}

export interface groupIdWithLinkColumn<T extends Object> extends ColumnBase<T> {
  type: "groupIdWithLink"
  custIdField: keyof T,
}

export interface CustomerInfoField {
  custId: number,
  groupName: string,
  tripId?: number
}

export interface linkToPpmColumn<T extends Object> extends ColumnBase<T> {
  type: "linkToPpm"
  linkField: keyof T
}

export interface LinkColumn<T extends Object> extends ColumnBase<T> {
  type: "link"
  linkField: keyof T,
  staticText?: string,
  target?: "_blank" | "_self" | "_parent" | "_top"
  linkType?: "tel" | "email" | "url"
}

export interface WebcastWithUrlColumn<T extends Object> extends ColumnBase<T> {
  type: "webcastWithUrl"
  linkField: keyof T
}

export interface FractionColumn<T extends Object> extends ColumnBase<T> {
  type: "fraction"
  denominatorField: keyof T
}

export interface ListColumn<T extends Object> extends ColumnBase<T> {
  type: "list"

}

export type SeverityMapValues = "danger" | "success" | "info" | "warn" | "primary" | "secondary" | "contrast"

export type TagSeverity = "danger" | "success" | "info" | "warn" | "secondary" | "contrast"

export function resolveTagSeverity(value: SeverityMapValues): TagSeverity {
  return value === "primary" ? "secondary" : value
}

export interface ChipColumn<T extends Object> extends ColumnBase<T> {
  type: "chip",
  severityMapField?: keyof T,
  severityMap?: { [prop: string]: SeverityMapValues },
}
